/* ============================================================
   CUSTOM MOTION SYSTEM — JS base compartilhado
   Vanilla, sem dependências. Reinicializa no editor de temas.
   Namespace exclusivo: window.CBMotion
   ============================================================ */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. REVEAL COM CASCATA ---------- */

  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver) return revealObserver;

    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('cb-is-in');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );

    return revealObserver;
  }

  function initReveal(scope) {
    var items = scope.querySelectorAll('[data-cb-reveal], .cb-mask');

    if (REDUCED) {
      items.forEach(function (el) {
        el.classList.add('cb-is-in');
      });
      return;
    }

    var groups = {};

    items.forEach(function (el) {
      if (el.dataset.cbBound === 'true') return;
      el.dataset.cbBound = 'true';

      // Cascata entre irmãos do mesmo container
      var parentKey = el.parentElement ? el.parentElement.dataset.cbGroup : null;
      if (parentKey) {
        groups[parentKey] = (groups[parentKey] || 0) + 1;
        el.style.setProperty('--cb-delay', (groups[parentKey] - 1) * 80 + 'ms');
      }

      // Rede de segurança: o que já está na tela aparece na hora,
      // sem depender do observador (que pode falhar em iframes/editor).
      // Só o que está abaixo da dobra espera o scroll.
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        // setTimeout (e não rAF): dispara mesmo sem frame composto,
        // e dá tempo do estado inicial pintar para a transição tocar
        window.setTimeout(function () {
          el.classList.add('cb-is-in');
        }, 80);
      } else {
        getRevealObserver().observe(el);
      }
    });
  }

  /* ---------- 2. SPLIT DE TEXTO POR PALAVRA ---------- */

  function initSplit(scope) {
    scope.querySelectorAll('[data-cb-split]').forEach(function (el) {
      if (el.dataset.cbSplitDone === 'true') return;
      el.dataset.cbSplitDone = 'true';

      var words = el.textContent.trim().split(/\s+/);
      el.textContent = '';

      words.forEach(function (word, i) {
        var wrap = document.createElement('span');
        wrap.className = 'cb-split__word';
        wrap.style.setProperty('--cb-i', i);

        var inner = document.createElement('span');
        inner.textContent = word;

        wrap.appendChild(inner);
        el.appendChild(wrap);

        if (i < words.length - 1) {
          el.appendChild(document.createTextNode(' '));
        }
      });
    });
  }

  /* ---------- 3. PARALLAX (rAF, só transform) ---------- */

  var parallaxItems = [];
  var parallaxTicking = false;

  function readParallax() {
    var vh = window.innerHeight;

    parallaxItems.forEach(function (item) {
      var rect = item.el.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > vh + 200) return;

      // -1 (acima da viewport) → 1 (abaixo)
      var progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
      var shift = progress * item.strength * -1;

      item.el.style.transform = 'translate3d(0, ' + shift.toFixed(2) + 'px, 0) scale(1.12)';
    });

    parallaxTicking = false;
  }

  function onParallaxScroll() {
    if (parallaxTicking) return;
    parallaxTicking = true;
    window.requestAnimationFrame(readParallax);
  }

  function initParallax(scope) {
    if (REDUCED) return;

    scope.querySelectorAll('[data-cb-parallax]').forEach(function (el) {
      if (el.dataset.cbBound === 'true') return;
      el.dataset.cbBound = 'true';

      parallaxItems.push({
        el: el,
        strength: parseFloat(el.dataset.cbParallax) || 60,
      });
    });

    if (parallaxItems.length && !window.CBMotion._parallaxBound) {
      window.CBMotion._parallaxBound = true;
      window.addEventListener('scroll', onParallaxScroll, { passive: true });
      window.addEventListener('resize', onParallaxScroll, { passive: true });
    }

    onParallaxScroll();
  }

  /* ---------- 4. CARROSSEL ARRASTÁVEL ---------- */

  function initRail(scope) {
    scope.querySelectorAll('[data-cb-rail]').forEach(function (rail) {
      if (rail.dataset.cbBound === 'true') return;
      rail.dataset.cbBound = 'true';

      var down = false;
      var startX = 0;
      var startScroll = 0;
      var moved = 0;

      // Só considera arraste acima deste limiar — abaixo disso é clique normal
      var DRAG_THRESHOLD = 10;

      rail.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch') return; // touch nativo já é bom
        down = true;
        moved = 0;
        startX = e.clientX;
        startScroll = rail.scrollLeft;
      });

      rail.addEventListener('pointermove', function (e) {
        if (!down) return;
        var delta = e.clientX - startX;
        moved = Math.abs(delta);

        if (moved > DRAG_THRESHOLD) {
          rail.classList.add('cb-is-dragging');
          rail.scrollLeft = startScroll - delta;
        }
      });

      ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (evt) {
        rail.addEventListener(evt, function () {
          down = false;
          rail.classList.remove('cb-is-dragging');
        });
      });

      // Cancela a navegação SÓ quando houve arraste de verdade.
      // Na fase de captura, antes do link receber o clique.
      rail.addEventListener(
        'click',
        function (e) {
          if (moved > DRAG_THRESHOLD) {
            e.preventDefault();
            e.stopPropagation();
          }
          moved = 0;
        },
        true
      );

      // Pontinhos de progresso
      var dots = rail.parentElement.querySelector('[data-cb-dots]');
      if (!dots) return;

      var buttons = dots.querySelectorAll('.cb-dots__item');
      if (!buttons.length) return;

      buttons.forEach(function (btn, i) {
        btn.addEventListener('click', function () {
          var pageWidth = rail.clientWidth;
          rail.scrollTo({ left: pageWidth * i, behavior: REDUCED ? 'auto' : 'smooth' });
        });
      });

      var syncTicking = false;

      rail.addEventListener(
        'scroll',
        function () {
          if (syncTicking) return;
          syncTicking = true;

          window.requestAnimationFrame(function () {
            var max = rail.scrollWidth - rail.clientWidth;
            var ratio = max > 0 ? rail.scrollLeft / max : 0;
            var active = Math.round(ratio * (buttons.length - 1));

            buttons.forEach(function (btn, i) {
              btn.classList.toggle('cb-is-active', i === active);
              btn.setAttribute('aria-current', i === active ? 'true' : 'false');
            });

            syncTicking = false;
          });
        },
        { passive: true }
      );
    });
  }

  /* ---------- 5. HOTSPOTS "SHOP THE LOOK" ---------- */

  function initHotspots(scope) {
    scope.querySelectorAll('[data-cb-hotspot]').forEach(function (spot) {
      if (spot.dataset.cbBound === 'true') return;
      spot.dataset.cbBound = 'true';

      var card = spot.querySelector('.cb-hotspot__card');
      if (!card) return;

      var open = function () {
        // fecha os outros antes de abrir este
        scope.querySelectorAll('[data-cb-hotspot].cb-is-open').forEach(function (other) {
          if (other !== spot) {
            other.classList.remove('cb-is-open');
            var b = other.querySelector('.cb-hotspot__dot');
            if (b) b.setAttribute('aria-expanded', 'false');
          }
        });
        spot.classList.add('cb-is-open');
      };

      var dot = spot.querySelector('.cb-hotspot__dot');

      if (dot) {
        dot.addEventListener('click', function (e) {
          e.preventDefault();
          var isOpen = spot.classList.contains('cb-is-open');
          if (isOpen) {
            spot.classList.remove('cb-is-open');
            dot.setAttribute('aria-expanded', 'false');
          } else {
            open();
            dot.setAttribute('aria-expanded', 'true');
          }
        });

        spot.addEventListener('mouseenter', open);
        spot.addEventListener('mouseleave', function () {
          spot.classList.remove('cb-is-open');
          dot.setAttribute('aria-expanded', 'false');
        });
        spot.addEventListener('focusout', function (e) {
          if (!spot.contains(e.relatedTarget)) {
            spot.classList.remove('cb-is-open');
            dot.setAttribute('aria-expanded', 'false');
          }
        });
      }
    });
  }

  /* ---------- 6. TROCA DE COR NO CARD (swatches) ---------- */

  function initSwatches(scope) {
    scope.querySelectorAll('[data-cb-swatch]').forEach(function (btn) {
      if (btn.dataset.cbBound === 'true') return;
      btn.dataset.cbBound = 'true';

      btn.addEventListener('click', function (e) {
        e.preventDefault();

        var card = btn.closest('[data-cb-card]');
        if (!card) return;

        var img = card.querySelector('[data-cb-card-primary]');
        var link = card.querySelector('[data-cb-card-link]');
        var newSrc = btn.dataset.cbSwatchImage;
        var newUrl = btn.dataset.cbSwatchUrl;

        // Variante sem foto própria: mantém a foto atual (só marca a bolinha e ajusta o link)
        if (img && newSrc && newSrc.length > 0) {
          img.style.opacity = '0';
          window.setTimeout(function () {
            img.src = newSrc;
            if (img.srcset) img.srcset = '';
            img.style.opacity = '1';
          }, 180);
        }

        if (link && newUrl) link.href = newUrl;

        card.querySelectorAll('[data-cb-swatch]').forEach(function (s) {
          s.classList.toggle('cb-is-active', s === btn);
          s.setAttribute('aria-pressed', s === btn ? 'true' : 'false');
        });
      });
    });
  }

  /* ---------- 7. COMPRA RÁPIDA (usa o cart drawer do Dawn) ---------- */

  function initQuickAdd(scope) {
    scope.querySelectorAll('[data-cb-quick-add]').forEach(function (btn) {
      if (btn.dataset.cbBound === 'true') return;
      btn.dataset.cbBound = 'true';

      btn.addEventListener('click', function (e) {
        e.preventDefault();

        var variantId = btn.dataset.cbQuickAdd;
        if (!variantId) return;

        var label = btn.querySelector('.cb-btn__label');
        var original = label ? label.textContent : '';
        if (label) label.textContent = 'Adicionando...';
        btn.disabled = true;

        var drawer = document.querySelector('cart-drawer');
        var body = { items: [{ id: Number(variantId), quantity: 1 }] };

        // Faz o Dawn re-renderizar o drawer junto com o add
        if (drawer && window.routes) {
          body.sections = 'cart-drawer,cart-icon-bubble';
          body.sections_url = window.location.pathname;
        }

        fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        })
          .then(function (r) {
            return r.json();
          })
          .then(function (data) {
            if (data.status) throw new Error(data.description || data.message);

            if (drawer && data.sections) {
              if (typeof drawer.renderContents === 'function') {
                drawer.renderContents(data);
              }
              if (typeof drawer.open === 'function') drawer.open();
            } else {
              window.location.href = (window.routes && window.routes.cart_url) || '/cart';
            }

            if (label) label.textContent = original;
          })
          .catch(function () {
            if (label) label.textContent = 'Indisponível';
            window.setTimeout(function () {
              if (label) label.textContent = original;
            }, 2000);
          })
          .finally(function () {
            btn.disabled = false;
          });
      });
    });
  }

  /* ---------- 8. HEADER QUE ENCOLHE AO ROLAR ---------- */

  function initHeaderShrink() {
    if (window.CBMotion._headerBound) return;
    window.CBMotion._headerBound = true;

    var lastY = 0;
    var ticking = false;

    function update() {
      var y = window.scrollY;
      document.documentElement.classList.toggle('cb-scrolled', y > 40);
      lastY = y;
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  /* ---------- BOOT ---------- */

  window.CBMotion = window.CBMotion || {};

  window.CBMotion.init = function (scope) {
    var root = scope || document;
    initReveal(root);
    initSplit(root);
    initParallax(root);
    initRail(root);
    initHotspots(root);
    initSwatches(root);
    initQuickAdd(root);
    initHeaderShrink();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      window.CBMotion.init();
    });
  } else {
    window.CBMotion.init();
  }

  // Editor de temas: seção adicionada / reordenada / editada
  document.addEventListener('shopify:section:load', function (e) {
    window.CBMotion.init(e.target);
  });

  document.addEventListener('shopify:section:select', function (e) {
    window.CBMotion.init(e.target);
  });
})();
